// Fila de avisos de lead atribuído (Closers e BDRs) — lógica pura sobre as
// notifications type='negotiation_assigned' que o trigger notify_negociacao_assignment
// grava (ver supabase/migrations/20260824160000_fix_notify_negociacao_assignment_metadata.sql).
//
// Um lead recém-atribuído não tem histórico de funil (consumo_medio_kwh normalmente
// ainda não foi preenchido), então NÃO reaproveita o score P1/P2/P3 de
// views/CRM/Kanban/kanbanWork.ts — usa os dois sinais que já chegam no metadata:
// reside em Goiás e a faixa de conta de luz autodeclarada.

import type { Notification } from '../../types';

export interface LeadAssignedMetadata {
    negociacao_id?: number;
    origem?: string | null;
    plataforma?: string | null;
    campanha?: string | null;
    anuncio?: string | null;
    formulario?: string | null;
    valor_conta_luz?: string | null;
    reside_goias?: boolean | null;
    negociacao_criada_em?: string | null;
}

export const TIPO_LEAD_ATRIBUIDO = 'negotiation_assigned' as const;

export const ehNotificacaoDeLeadAtribuido = (n: Notification): boolean =>
    n.type === TIPO_LEAD_ATRIBUIDO && !!n.metadata?.negociacao_id;

/** Maior faixa = conta de luz mais alta = lead mais "quente". Sem faixa informada fica por último. */
const RANK_FAIXA_CONTA: Record<string, number> = {
    'acima_de_r$700': 2,
    'de_r$300_a_r$700': 1,
    'menos_de_r$300': 0,
};

const rankFaixaConta = (n: Notification): number => {
    const faixa = (n.metadata as LeadAssignedMetadata | undefined)?.valor_conta_luz;
    return faixa ? (RANK_FAIXA_CONTA[faixa] ?? -1) : -1;
};

const resideGoias = (n: Notification): boolean =>
    (n.metadata as LeadAssignedMetadata | undefined)?.reside_goias === true;

/**
 * Ordena a fila represada: reside em Goiás primeiro, depois faixa de conta de luz
 * mais alta, e por fim mais antigo primeiro (quem está esperando há mais tempo).
 * Não muta o array de entrada.
 */
export function ordenarNotificacoesLead(notifications: Notification[]): Notification[] {
    return [...notifications].sort((a, b) => {
        const goiasA = resideGoias(a) ? 1 : 0;
        const goiasB = resideGoias(b) ? 1 : 0;
        if (goiasA !== goiasB) return goiasB - goiasA;

        const faixaA = rankFaixaConta(a);
        const faixaB = rankFaixaConta(b);
        if (faixaA !== faixaB) return faixaB - faixaA;

        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
}

/** IDs de negociação com aviso de atribuição ainda não visto — liga o pulso do card no Kanban. */
export function negociacoesNaoAbertas(notifications: Notification[]): Set<number> {
    const ids = new Set<number>();
    for (const n of notifications) {
        if (n.read || !ehNotificacaoDeLeadAtribuido(n)) continue;
        const id = (n.metadata as LeadAssignedMetadata | undefined)?.negociacao_id;
        if (typeof id === 'number') ids.add(id);
    }
    return ids;
}
